import { useState, useCallback, useEffect } from 'react';
import { useOrganizationContext } from '../../../shared/contexts/OrganizationContext';
import { supabase } from '../../../shared/services/supabase';
import type { 
  Organization, 
  OrganizationMember, 
  OrganizationClient,
  OrganizationMemberRole 
} from '../../../shared/types/organization.types';

interface UseOrganizationResult {
  // Current organization
  organization: Organization | null;
  role: OrganizationMemberRole | null;
  isLoading: boolean;
  
  // Organization data
  members: OrganizationMember[];
  clients: OrganizationClient[];
  membersLoading: boolean;
  clientsLoading: boolean;
  
  // Actions
  fetchMembers: () => Promise<void>;
  fetchClients: () => Promise<void>;
  switchOrganization: (orgId: string) => void;
  
  // Helpers
  isAgency: boolean;
  isBrand: boolean;
}

/**
 * Hook to access the current active organization and its data.
 * 
 * Usage:
 * const { organization, role, members, clients, isAgency } = useOrganization();
 */
export function useOrganization(): UseOrganizationResult {
  const { 
    activeOrganization, 
    activeRole, 
    isLoading,
    switchOrganization 
  } = useOrganizationContext();

  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [clients, setClients] = useState<OrganizationClient[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [clientsLoading, setClientsLoading] = useState(false);

  // Fetch organization members
  const fetchMembers = useCallback(async () => {
    if (!activeOrganization) {
      setMembers([]);
      return;
    }

    setMembersLoading(true);
    try {
      const { data, error } = await supabase
        .from('organization_members')
        .select(`
          id,
          profile_id,
          role,
          invited_at,
          accepted_at,
          profile_picture_url,
          profile:profiles!organization_members_profile_id_fkey(
            id,
            username,
            email
          )
        `)
        .eq('organization_id', activeOrganization.id)
        .order('role');

      if (error) throw error;

      setMembers((data || []).map(m => ({
        id: m.id,
        profile_id: m.profile_id,
        role: m.role as OrganizationMemberRole,
        invited_at: m.invited_at,
        accepted_at: m.accepted_at,
        profile_picture_url: m.profile_picture_url || null,
        profile: m.profile as unknown as OrganizationMember['profile'],
      })));
    } catch (err) {
      console.error('Error fetching members:', err);
      setMembers([]);
    } finally {
      setMembersLoading(false);
    }
  }, [activeOrganization]);

  // Fetch organization clients
  const fetchClients = useCallback(async () => {
    if (!activeOrganization) {
      setClients([]);
      return;
    }

    setClientsLoading(true);
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('organization_id', activeOrganization.id)
        .order('name');

      if (error) throw error;

      setClients((data || []) as OrganizationClient[]);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setClients([]);
    } finally {
      setClientsLoading(false);
    }
  }, [activeOrganization]);

  // Clear data when org changes
  useEffect(() => {
    setMembers([]);
    setClients([]);
  }, [activeOrganization?.id]);

  return {
    organization: activeOrganization,
    role: activeRole,
    isLoading,
    members,
    clients,
    membersLoading,
    clientsLoading,
    fetchMembers,
    fetchClients,
    switchOrganization,
    isAgency: activeOrganization?.type === 'agency',
    isBrand: activeOrganization?.type === 'brand',
  };
}
